import {DocumentPreview} from "@/components/ui/forms/DocumentPreview";
import {MaskList} from "@/components/ui/forms/MaskList";

import {useCallback, useEffect, useRef, useState} from "react";

import {isEqual} from "@helpers";
import {useLevelOrder} from "@hooks/LevelOrder";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {UploadFilePopup} from "@popups/uploadPopups/UploadFilePopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {
  clearCreate,
  createProduct,
  current,
  setError,
  setIsChanged,
  setTarget,
} from "@redux/slices/productSlices/createSlice";
import {
  clearOptions,
  getCountries,
  getProductLine,
} from "@redux/slices/productSlices/optionsSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {LevelDocuments} from "@ui/forms/LevelDocuments";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const ProductCreatePage = () => {
  const {productLevels} = useLevelOrder();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {openPopup} = usePopup();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {countries, productLine} = useSelector(
    (state) => state.product.options,
  );
  const {target, status} = useSelector((state) => state.product.create);

  const [fileList, setFileList] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);

  const productRef = useRef();

  const updateProductField = useCallback(
    (fieldType, value) => {
      dispatch(
        setTarget({
          ...target,
          [fieldType]: value,
        }),
      );
    },
    [target, dispatch],
  );

  const updateProductVersionField = useCallback(
    (fieldType, value) => {
      dispatch(
        setTarget({
          ...target,
          product_version: {
            ...target.product_version,
            [fieldType]: value,
          },
        }),
      );
    },
    [target, dispatch],
  );

  const handleClickCover = useCallback(
    (color) => {
      updateProductField("mask", color);
    },
    [updateProductField],
  );

  const handleClickUploadImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleClickUploadFile = (level) => {
    openPopup(
      <UploadFilePopup
        fileList={fileList}
        setFileList={setFileList}
        level={level}
      />,
    );
  };

  const handleClickRemoveFile = (fileName, level) => {
    setFileList((prevFileList) =>
      prevFileList.filter(
        (file) => !(file.level === level && file.file.name === fileName),
      ),
    );
  };

  const handleClickRemovePhoto = () => {
    setPhotoFile(null);
  };

  const handleClickSave = async () => {
    if (
      !groupedFiles.some((file) => file.level === "easy") ||
      !target.mask ||
      !target.name ||
      !target.country_id ||
      !target.product_line_id
    ) {
      openPopup(<FillDataPopup />);
    } else {
      const product = {
        product: {
          name: target.name,
          country_id: target.country_id,
          product_line_id: target.product_line_id,
          ...(photoFile && {cover: photoFile}),
          mask: target.mask,
        },
        product_version: {
          questions: target.product_version.questions,
          files: fileList,
        },
      };

      const resultAction = await dispatch(createProduct({product}));

      if (createProduct.fulfilled.match(resultAction)) {
        openPopup(<SuccessfulSavePopup title={t("popup.save_data.product")} />);
        setFileList([]);
        setPhotoFile(null);
        dispatch(clearCreate());
      } else {
        dispatch(setError("Save error!"));
      }
    }
  };

  const groupedFiles = productLevels
    .map((level) => ({
      level: level.value,
      file: fileList.filter((file) => file.level === level.value),
    }))
    .filter((item) => item.file.length > 0);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getProductLine());

    return () => {
      dispatch(clearCreate());
      dispatch(clearOptions());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setIsChanged(!isEqual(target, current)));
  }, [target, dispatch]);

  useEffect(() => {
    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
      }
    };
  }, [photoFile]);

  useEffect(() => {
    if (productRef.current && isMobile) {
      productRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={productRef}
      className={styles.productWrapper}
    >
      <BorderArticle>
        <div className={styles.product}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.productContent}>
              <div className={styles.productContentHeadWrapper}>
                <div className={styles.productEditContent}>
                  <div className={styles.productContentHead}>
                    <EditTitle
                      label={"product-name"}
                      value={target?.name}
                      placeholder={t("input.product_name_placeholder")}
                      handleClickSave={handleClickSave}
                      disabledButton={false}
                      onChange={(e) =>
                        updateProductField("name", e.target.value)
                      }
                    />

                    <ViewField
                      label={`${t("input.background")}*`}
                      customContent={
                        <MaskList onClickCover={handleClickCover} />
                      }
                    />

                    <ViewField
                      label={t("input.cover")}
                      customContent={
                        <div
                          className={"flex flex-col items-start gap-[16rem]"}
                        >
                          {photoFile && (
                            <DocumentPreview
                              name={photoFile?.name}
                              onClickCross={handleClickRemovePhoto}
                            />
                          )}

                          <ControlButton
                            color={"var(--violetLight)"}
                            txt={t("button.upload_image")}
                            onClick={handleClickUploadImage}
                          />
                        </div>
                      }
                    />
                  </div>

                  <ViewField
                    label={t("input.preview")}
                    direction={"col"}
                    customContent={
                      <PreviewCard
                        name={target?.name}
                        mask={target?.mask}
                        image={
                          photoFile ? URL.createObjectURL(photoFile) : undefined
                        }
                      />
                    }
                  />
                </div>
              </div>

              <ul className={`${styles.productContentBody} ${styles.editBody}`}>
                <EditField
                  label={`${t("input.country")}*`}
                  width={250}
                  height={150}
                  searchPlaceholder={t("input.country_search")}
                  activeOption={target?.country}
                  options={countries}
                  onSelect={(selectedOption) =>
                    updateProductField("country_id", selectedOption.id)
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.product_line")}*`}
                  width={250}
                  height={150}
                  searchPlaceholder={t("input.product_line_search")}
                  activeOption={target?.product_line}
                  options={productLine}
                  onSelect={(selectedOption) =>
                    updateProductField("product_line_id", selectedOption?.id)
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.related_documents_upload")}*`}
                  customContent={
                    <div className={styles.productLevelList}>
                      {productLevels &&
                        productLevels.map((level) => {
                          const levelFiles =
                            groupedFiles?.find(
                              (item) => item?.level === level.value,
                            )?.file || [];

                          return (
                            <LevelDocuments
                              key={level.id}
                              level={level.value}
                              files={levelFiles}
                              onRemoveFile={handleClickRemoveFile}
                              onUploadFile={() => handleClickUploadFile(level)}
                              editable
                            />
                          );
                        })}
                    </div>
                  }
                  type={"custom"}
                />

                <EditField
                  label={t("input.questions")}
                  value={target?.product_version?.questions}
                  onChange={(e) =>
                    updateProductVersionField("questions", e.target.value)
                  }
                  type={"textarea"}
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
